import { getTokenFromLocal, handleResponse } from "../../../_helpers/handleResponseMiddleware";

// Base URLs — reuse the SAME env vars the existing central module uses, so calls
// hit the correct node service instead of the java host.
//   REACT_APP_CENTRAL_UPLOAD_URL       -> node doc service root (central-dashboard/*, bd-dashboard/*, finance reads)
//   REACT_APP_CENTRAL_BASE_URL         -> already ends with /central-dashboard-routing
const centralNodeBaseUrl = process.env.REACT_APP_CENTRAL_UPLOAD_URL;      // e.g. https://pristyntech.com/otp_test
const centralRoutingUrl = process.env.REACT_APP_CENTRAL_BASE_URL;        // e.g. https://pristyntech.com/otp_test/central-dashboard-routing

const docServiceUrl = centralNodeBaseUrl;                                // central-dashboard/* + bd-dashboard/*

// Finance reads (get-collection-details, get-collection-status-history) live on a
// separate AWS Lambda (cityops_dashboard), NOT the node service. Set via env.
const financeBaseUrl = process.env.REACT_APP_FINANCE_BASE_URL;           // e.g. https://<lambda-id>.execute-api.ap-south-1.amazonaws.com/production/cityops_dashboard

function authHeaders(contentType = "application/json") {
  const user = getTokenFromLocal();
  const token = user ? user.access_token : null;
  const headers = {
    Authorization: `Bearer ${token}`,
    mobileplatform: "web",
  };
  if (contentType) headers["Content-Type"] = contentType;
  return headers;
}

// Finance reads hit a separate AWS Lambda (cityops_dashboard) whose CORS config
// only allows standard headers. Sending custom headers (mobileplatform / appname)
// triggers a CORS preflight that the Lambda rejects. Keep these requests to
// simple headers only so no disallowed header is sent.
function financeAuthHeaders() {
  const user = getTokenFromLocal();
  const token = user ? user.access_token : null;
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

// 6.1 — Get uploaded final-bill docs.
// Mirrors the central dashboard exactly: POST {docServiceUrl}/central-dashboard/getFinalBillCentral
// with body { app_id }. Response: { message: 'Success', status: 1, data: { images: [...],
// collectionDetails, isDetailedBillRequired, status } }.
export function getFinalBillCentral(appId) {
  return fetch(`${docServiceUrl}/central-dashboard/getFinalBillCentral`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ app_id: appId }),
  }).then(handleResponse);
}

// 6.2 — Upload document metadata
export function uploadFinalBillCentral(payload) {
  return fetch(`${docServiceUrl}/central-dashboard/uploadFinalBillCentral`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  })
    .then(handleResponse);
}

// 6.3 — Update insurance dashboard net bill
export function updateInsDashboard(payload) {
  return fetch(`${docServiceUrl}/bd-dashboard/updateInsDashboard`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  })
    .then(handleResponse);
}

// 6.4 — Raw file upload (multipart)
export function uploadFiles(formData) {
  return fetch(`${docServiceUrl}/bd-dashboard/uploadFiles`, {
    method: "POST",
    headers: authHeaders(null), // no content-type for multipart
    body: formData,
  })
    .then(handleResponse);
}

// 6.5 — Sign a storage URL.
// Use the SAME proven endpoint the existing central module uses
// (`centralGetSignedUrlService`): POST {docServiceUrl}/EMR/getSignedUrl
// Response: { status: "Success", result: ["<signed-url>"], description }
export function getSignedUrl(urls) {
  return fetch(`${docServiceUrl}/EMR/getSignedUrl`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ urls }),
  }).then(handleResponse);
}

// 6.6 — Update central-dashboard field
export function updateCentralDashReporting(payload) {
  return fetch(
    `${centralRoutingUrl}/updateCentralDashRepoting`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }
  )
    .then(handleResponse);
}

// 6.7 — Search / switch appointment
export function fetchFinalBill(searchAppId) {
  return fetch(`${docServiceUrl}/central-dashboard/fetchFinalBill`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      search: searchAppId,
      searchKey: "Appointment Id",
      key: "finalBill",
      tab_name: "postdischarge",
      filter_status: true,
      page_number: 1,
    }),
  })
    .then(handleResponse);
}

// 6.8 — Collection details
export function getCollectionDetails(appointmentId) {
  return fetch(
    `${financeBaseUrl}/get-collection-details?appointmentId=${appointmentId}`,
    { method: "GET", headers: financeAuthHeaders() }
  )
    .then(handleResponse);
}

// 6.8 — Payment history
export function getPaymentHistory(appointmentId) {
  return fetch(
    `${centralRoutingUrl}/getPaymentHistory`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ appointmentId }),
    }
  )
    .then(handleResponse);
}

// 6.8 — DA documents
export function getDaDocuments(leadId) {
  return fetch(
    `${docServiceUrl}/central-dashboard/get-da-documents?leadId=${leadId}`,
    { method: "GET", headers: authHeaders() }
  )
    .then(handleResponse);
}

// 6.8 — Collection gap reason
export function getCollectionGapReason(appId) {
  return fetch(`${docServiceUrl}/central-dashboard/getCollectionGapReason`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ appId }),
  })
    .then(handleResponse);
}

// 6.8 — Save collection entry
export function saveCollectionEntry(payload) {
  return fetch(`${docServiceUrl}/central-dashboard/save-collection-entry`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  })
    .then(handleResponse);
}

// 6.9 — Collection history
export function getCollectionStatusHistory(appointmentId) {
  return fetch(
    `${financeBaseUrl}/get-collection-status-history?appointmentId=${appointmentId}`,
    { method: "GET", headers: financeAuthHeaders() }
  )
    .then(handleResponse);
}
